import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError } from "@refinedev/core";
import Button from "components/button";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import Select from "components/select";
import { SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

interface AddTemplateModalProps extends IModalProps {
  index: number;
  groupIndex: number;
  groupFields: any[];
  languages: SupportedLanguage[];
  onSubmit: (index: number, targetGroupIndex: number) => void;
}

const MoveGroupSchema = z.object({
  index: z.string().min(1, "This field is required")
});

export const MoveFieldModal = ({
  open,
  onClose,
  index,
  groupIndex,
  groupFields,
  languages,
  onSubmit,
}: AddTemplateModalProps) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<{ index: string }, HttpError, { index: string }>({
    defaultValues: {
      index: ""
    },
    resolver: zodResolver(MoveGroupSchema),
  });

  useEffect(() => {
    if (open) {
      reset({index: ""});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open]);

  const _submit = (values: { index: string }) => {
    onSubmit(index, Number(values.index));
    onClose();
  };

  const groupList = groupFields.map((group, groupDataIndex) => (
    { value: groupDataIndex.toString(), label: `${languages.map((lang) => get(group, `group.label.${lang}`, "")).join(" • ")}`}
  ));

  return (
    <Modal title="Move group info" onClose={onClose} open={open}>
      <div>
        <div className="w-full divide-y divide-gray-100">
          <div className="box-container">
            <p className="text-sm mb-4"><span className="font-medium">Current group info:</span> {groupList.find(group => group.value === groupIndex.toString())?.label ?? ""} </p>
            <Label
              required
              id={`move_group`}
              name="Move to"
            />

            <Controller
              control={control}
              name={`index`}
              render={({ field: { onChange, value, ref } }) => {
                return (
                  <Select
                    key={`index`}
                    inputRef={ref}
                    options={groupList.filter(groupData => groupData.value !== groupIndex.toString())}
                    id={`index`}
                    placeholder="Select group info"
                    value={groupList.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    ) ?? ""}
                    onChange={(selected: { label: string; value: any }, {action} : any) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors,`index`, "")}
                  />
                );
              }}
            />

            {errors["index"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["index"].message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="w-full box-container py-4">
            <div className="flex items-center justify-end gap-x-3">
              <Button
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button className="w-20" onClick={handleSubmit(_submit)}>
                Move
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
};
